import { useState, useEffect } from 'react'
import { useHistory } from 'react-router'
import { useQueryParam } from 'use-query-params'
import { Grid, Typography, makeStyles, Container, Button, Box } from '@material-ui/core'
import GetAppRoundedIcon from '@material-ui/icons/GetAppRounded'
import { convertDateToFormatTH, convertDateToFormatEN } from '../utilities/date'
import useTranslate from '../i18n/useTranslate'
import CertificateSummaryPanel from '../component/report/certificate-summary/Panel'
import certificateSummaryExport from '../component/report/certificate-summary/report'
import useAuthenStore from '../stores/authen/authenStore'
import SearchAccordion from '../control/accordion/SearchAccordion'
import Table from '../component/report/certificate-summary/Table'
import useLoading from '../control/loading/useLoading'
import {
    useCertificateSummary,
    CertificateSummary,
    CertificateRequest,
    CertificateDetail,
} from '../services/um/useCertificateSummary'

const CertificateSummaryForm = () => {
    const translate = useTranslate()
    const lang = translate.languageStore.currentLanguage as 'th' | 'en'
    const classes = useStyles()
    const history = useHistory()
    const authenStore = useAuthenStore()
    const loadingHook = useLoading()
    const { getCertificates, getCertificateSummary } = useCertificateSummary()

    const [dataList, setDataList] = useState<CertificateDetail[]>([])
    const [certificateSummary, setCertificateSummary] = useState<CertificateSummary[]>([])

    const [certificateYear, setCertificateYear] = useQueryParam<number | undefined>('certificateYear')
    const [certificateType, setCertificateType] = useQueryParam<string | undefined>('certificateType')

    const setSearchParam = (value: CertificateRequest) => {
        setCertificateYear(value.certificateYear)
        setCertificateType(value.certificateType)
    }

    const [initialValue, setInitialValue] = useState<CertificateRequest>({
        certificateYear: certificateYear ?? new Date().getFullYear(),
        certificateType: certificateType,
        userID: authenStore.profile.userId ?? 0,
    })

    useEffect(() => {
        if (authenStore.profile.userId) {
            onSubmit(initialValue)
        }
    }, [authenStore.profile.userId])

    const onSubmit = async (value: CertificateRequest) => {
        loadingHook.loadingOpen()
        try {
            setInitialValue(value)
            const data = await getCertificates({
                certificateYear: value.certificateYear,
                certificateType: value.certificateType,
                userID: authenStore.profile.userId ?? 0,
            })
            setDataList(data ?? [])
            setSearchParam(value)
            await fetchCertificateSummary(value.certificateYear)
        } finally {
            loadingHook.loadingClose()
        }
    }

    const fetchCertificateSummary = async (year?: number) => {
        const data = await getCertificateSummary({
            certificateYear: year,
            userID: authenStore.profile.userId ?? 0,
        })
        setCertificateSummary(data ?? [])
    }

    const onClear = async (form: any) => {
        form.reset()
        const cleared: CertificateRequest = {
            certificateYear: undefined,
            certificateType: undefined,
            userID: authenStore.profile.userId ?? 0,
        }
        await onSubmit(cleared)
    }

    const convertDate = (date: string | null) => {
        if (lang === 'th' && date) {
            return convertDateToFormatTH(date)
        } else if (lang === 'en' && date) {
            return convertDateToFormatEN(date)
        } else {
            return '-'
        }
    }

    const onBack = () => {
        history.push('/dashboard')
    }

    const handleExportExcel = () => {
        if (!dataList.length) return
        certificateSummaryExport({
            header: {
                certificateYear: certificateYear ? +certificateYear + 543 : null,
                certificateType: certificateType ?? '-',
            },
            content: dataList.map((item) => ({
                projectID: item.projectNumber,
                receiveDate: convertDate(item.receiveDate),
                endDate: convertDate(item.closedDate),
                researcherName: item.name?.[lang] ?? '-',
                projectNameTH: item.projectName?.['th'] ?? '-',
                projectNameEN: item.projectName?.['en'] ?? '-',
                projectType: item.projectType?.name?.[lang] ?? '-',
                certificateNo: item.certificateNo ?? '-',
                certificateType: item.certificateType ?? '-',
            })),
            translate: (value: string) => translate.get(value),
        })
    }

    return (
        <Container maxWidth="lg">
            <Grid className={classes.root}>
                <SearchAccordion
                    title={translate.get('UM.CERTIFICATE_SUMMARY.HEADER')}
                    isBack={true}
                    onClickBack={onBack}
                >
                    <CertificateSummaryPanel onSubmit={onSubmit} initialValue={initialValue} onClear={onClear} />
                </SearchAccordion>

                <Grid container spacing={0} className={classes.cardRow}>
                    {certificateSummary?.map((c: CertificateSummary, i) => (
                        <Grid key={i} item xs={12} md={6} className={classes.cardCol}>
                            <Box
                                className={classes.card}
                                style={{
                                    backgroundColor: /COA/i.test(c.label.en) ? '#7BC36E' : '#8C63C7',
                                }}
                            >
                                <Typography variant="subtitle1" className={classes.cardLabel}>
                                    {c.label?.[lang] ?? c.label.en}
                                </Typography>
                                <Typography variant="h3" className={classes.cardValue}>
                                    {c.value ?? 0}
                                </Typography>
                            </Box>
                        </Grid>
                    ))}
                </Grid>

                <Grid container justify="space-between" alignItems="flex-end" className={classes.container}>
                    <Grid item xs={12} md={6}>
                        <Typography variant="h6">
                            {translate.get('LABEL.SEARCH_TOTAL_RESULT', [`${dataList.length}`])}
                        </Typography>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Grid container justify="flex-end" spacing={2}>
                            <Grid item>
                                <Button
                                    size="large"
                                    variant="outlined"
                                    color="primary"
                                    startIcon={
                                        <GetAppRoundedIcon style={{ color: dataList.length ? '#DB5F8E' : '#e0e0e0' }} />
                                    }
                                    disabled={!dataList.length}
                                    onClick={handleExportExcel}
                                >
                                    <Typography variant="button">{translate.get('BUTTON.EXPORT')}</Typography>
                                </Button>
                            </Grid>
                        </Grid>
                    </Grid>

                    <Grid item xs={12}>
                        <Table dataList={dataList} />
                    </Grid>
                </Grid>
            </Grid>
        </Container>
    )
}

const useStyles = makeStyles(() => ({
    root: {
        width: '100%',
    },
    container: {
        margin: '15px 0px',
        width: '100%',
    },

    cardWrapper: {
        padding: 0,
        marginTop: '15px',
    },
    cardRow: {
        marginTop: 15,
        display: 'flex',
        flexWrap: 'wrap',
        gap: 16,
    },
    cardCol: {
        padding: 0,
        flex: '1 1 100%',
        '@media (min-width:960px)': { flex: '1 1 calc(50% - 8px)' },
    },
    card: {
        borderRadius: 8,
        padding: '12px 16px',
        color: '#fff',
        textAlign: 'center',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        minHeight: 110,
    },
    cardLabel: {
        fontWeight: 600,
        marginBottom: 16,
        marginRight: 'auto',
    },
    cardValue: {
        fontWeight: 700,
        marginBottom: 12,
    },
}))

export default CertificateSummaryForm
